import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { InlineMessage } from '../ui';
import type { ApplyMatchSkip } from '../../services/integrationApi';
import { groupApplySkips } from './applyMatchesResult';
import { applyMatchSkipReason, retryableMatchSkips } from './applyMatchReason';
import { settingsAnchorForGateCode, syncBehaviorSettingUrl } from './skipReasonActions';

const T = 'integrations.catalogScan.skips';
const KNOWN_CODES = ['pro_only_excluded', 'refurbished_excluded', 'rental_excluded'];

interface ApplyMatchesSkipsProps {
  integrationId: string;
  skips: ApplyMatchSkip[];
  /** Linked, then the Shopify content write failed. */
  updateFailures?: ApplyMatchSkip[];
  /** canonicalProductId → the name shown in the scan. */
  names?: Record<string, string>;
  onRetry?: (skips: ApplyMatchSkip[]) => void;
  retrying?: boolean;
}

const ProductLines: React.FC<{
  rows: ApplyMatchSkip[];
  names?: Record<string, string>;
  phase: 'link' | 'update';
}> = ({ rows, names, phase }) => {
  const { t } = useAppTranslation();
  if (rows.length === 0) return null;
  return (
    <Box sx={{ maxHeight: 280, overflow: 'auto', mt: 1 }} role="list">
      {rows.map((skip) => {
        const name = names?.[skip.canonicalProductId] || skip.canonicalProductId;
        return (
          <Box key={`${skip.canonicalProductId}:${skip.code}`} role="listitem" sx={{ py: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{name}</Typography>
            <Typography variant="body2" color="text.secondary">{applyMatchSkipReason(skip, t, phase)}</Typography>
          </Box>
        );
      })}
    </Box>
  );
};

/**
 * Every match an Apply left unlinked, one row per product with the reason, plus the catalog-gate
 * setting that would let a pro-only, refurbished or rental product through. Update failures are
 * the rows that did link and then could not be written to Shopify.
 */
const SkipLines: React.FC<ApplyMatchesSkipsProps> = ({
  integrationId,
  skips,
  updateFailures = [],
  names,
  onRetry,
  retrying = false,
}) => {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const gateGroups = groupApplySkips(skips.filter((skip) => KNOWN_CODES.includes(skip.code)));
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {skips.length > 0 && (
        <InlineMessage severity="warning">
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {t(`${T}.title`, { count: skips.length })}
          </Typography>
          {gateGroups.map(({ code, count }) => {
            const anchor = settingsAnchorForGateCode(code);
            return (
              <Box key={code} sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 1 }}>
                <Typography variant="body2">{t(`${T}.${KNOWN_CODES.includes(code) ? code : 'other'}`, { count })}</Typography>
                {anchor && (
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => navigate(syncBehaviorSettingUrl(integrationId, anchor))}
                    sx={{ fontWeight: 600, minWidth: 0, p: 0 }}
                  >
                    {t(`${T}.action.${code}`)}
                  </Button>
                )}
              </Box>
            );
          })}
          <ProductLines rows={skips} names={names} phase="link" />
          {onRetry && retryableMatchSkips(skips).length > 0 && (
            <Button size="small" variant="outlined" disabled={retrying} onClick={() => onRetry(retryableMatchSkips(skips))} sx={{ mt: 1 }}>
              {t('integrations.catalogScan.retryFailed', { count: retryableMatchSkips(skips).length })}
            </Button>
          )}
        </InlineMessage>
      )}
      {updateFailures.length > 0 && (
        <InlineMessage severity="warning">
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {t('integrations.catalogScan.updateFailuresTitle', { count: updateFailures.length })}
          </Typography>
          <ProductLines rows={updateFailures} names={names} phase="update" />
          {onRetry && retryableMatchSkips(updateFailures).length > 0 && (
            <Button
              size="small"
              variant="outlined"
              disabled={retrying}
              onClick={() => onRetry(retryableMatchSkips(updateFailures))}
              sx={{ mt: 1 }}
            >
              {t('integrations.catalogScan.retryFailed', { count: retryableMatchSkips(updateFailures).length })}
            </Button>
          )}
        </InlineMessage>
      )}
    </Box>
  );
};

// The router hook lives in SkipLines, so a screen with nothing to report needs no router.
const ApplyMatchesSkips: React.FC<ApplyMatchesSkipsProps> = (props) =>
  props.skips.length === 0 && (props.updateFailures?.length ?? 0) === 0 ? null : <SkipLines {...props} />;

export default ApplyMatchesSkips;
